import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { Theme, makeStyles } from '@material-ui/core/styles';
import { Container, Divider, StepLabel, Step, Stepper } from '@material-ui/core';
import { SweetAlertOptions } from 'sweetalert2';
import StepActionBar from 'components/@common/Stepper/StepActionBar';
import StepContainer from 'components/@common/Stepper/StepContainer';
import StepDetails from 'components/@common/Stepper/StepDetails';
import StepSubheader from 'components/@common/Stepper/StepSubheader';
import PageContainer from 'components/@layout/PageContainer';
import GET_CLASSROOMS_MANAGEMENT_QUERY from 'graph/classroom/get-classrooms-management-query';
import GET_USER_CLASSROOMS from 'graph/classroom/get-user-classrooms-query';
import { ModificationAlertContent } from 'hooks/sweet-alert/use-modification-alert';
import useStore from 'store';
import {
  Classroom,
  Organization,
  ClassroomLicenseUsage,
  ProductCurriculum
} from 'types/schema-generated-types';
import { getEducatorNames } from 'utilities/get-classroom-educators';
import { RemoveVariant } from '../../../types';
import ClassroomSelectTable from './components/ClassroomSelectTable';
import { ClassroomRowData } from './components/ClassroomSelectRow';

export interface RemoveClassroomModalProps {
  onClose: () => void;
  onConfirm: (params: { overrides: SweetAlertOptions }, classroomIds: number[]) => void;
  variant?: RemoveVariant;
  admin?: boolean;
}

const STEPS = [
  { id: 'select', titleI18nKey: 'selectClassesStepTitle' },
  { id: 'review', titleI18nKey: 'reviewStepTitle' }
];

const VARIANT_LABEL_MAP = {
  [RemoveVariant.ARCHIVE]: {
    titleI18nKey: 'archiveTitle',
    instructionsI18nKey: 'archiveInstructions',
    selectButtonI18nKey: 'archiveTitle',
    reviewInstructionsI18nKey: 'archiveReviewInstructions',
    confirmInstructionsI18nKey: null
  },
  [RemoveVariant.DELETE]: {
    titleI18nKey: 'deleteTitle',
    instructionsI18nKey: 'deleteInstructions',
    selectButtonI18nKey: 'deleteTitle',
    reviewInstructionsI18nKey: 'deleteReviewInstructions',
    confirmInstructionsI18nKey: 'deleteConfirmInstructions'
  }
};

const useStyles = makeStyles((theme: Theme) => {
  const STEPPER_HEIGHT = 24;
  return {
    root: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    },
    stepper: {
      height: STEPPER_HEIGHT,
      margin: '0 auto',
      paddingLeft: '10%',
      paddingBottom: theme.spacing(4),
      paddingTop: 0,
      width: '40%'
    },
    stepLabel: {
      fontWeight: 600,
      textTransform: 'uppercase',
      '&.MuiStepLabel-active': {
        fontWeight: 600,
        color: `${theme.palette.secondary.main} !important`
      }
    },
    stepIcon: {
      color: `${theme.palette.secondary.main} !important`
    },
    body: {
      height: `100%`,
      overflow: 'auto',
      boxSizing: 'border-box',
      paddingBottom: theme.spacing(3)
    },
    filterBarContainer: {
      textAlign: 'center',
      margin: 'auto 0',
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1.5)
    },
    clearIcon: {
      fontSize: theme.typography.pxToRem(16),
      marginRight: theme.spacing(1)
    }
  };
});

const RemoveClassroomModal: React.FC<RemoveClassroomModalProps> = ({
  onClose,
  onConfirm,
  variant = RemoveVariant.ARCHIVE,
  admin
}) => {
  const { t } = useTranslation('RemoveClassroomModal');
  const [{ user }] = useStore();
  const classes = useStyles();
  const [currentStep, setCurrentStep] = React.useState<0 | 1>(0);
  const { data, loading, error } = useQuery(
    admin ? GET_CLASSROOMS_MANAGEMENT_QUERY : GET_USER_CLASSROOMS,
    admin ? {} : { variables: { userId: user.me?.id } }
  );

  const classrooms: Classroom[] =
    data?.classrooms ?? data?.organizations?.flatMap((o: Organization) => o.classrooms ?? []) ?? [];

  const rowData: ClassroomRowData[] = (variant === RemoveVariant.ARCHIVE
    ? classrooms.filter(x => !x.dateArchived)
    : classrooms
  ).map(x => {
    const { educator, coeducators } = getEducatorNames(x);
    const licenseUsage: ClassroomLicenseUsage[] = x.licenseUsage ?? [];

    return {
      id: x.id,
      name: x.name,
      educator,
      coEducator: coeducators,
      status: x.dateArchived ? 'ARCHIVED' : 'ACTIVE',
      productCurriculums: licenseUsage
        .filter(u => !!u.productCurriculum)
        .map(usage => usage.productCurriculum) as ProductCurriculum[]
    };
  });

  const [selectedClassroomIds, setSelectedClassroomIds] = React.useState<number[]>([]);
  const [confirmRowData, setConfirmRowData] = React.useState<ClassroomRowData[]>(rowData);

  const {
    titleI18nKey,
    instructionsI18nKey,
    selectButtonI18nKey,
    reviewInstructionsI18nKey,
    confirmInstructionsI18nKey
  } = VARIANT_LABEL_MAP[variant];

  const handleNext = () => {
    if (currentStep === 0) {
      setCurrentStep(1);
      setConfirmRowData(rowData.filter(x => selectedClassroomIds.includes(x.id)));
    }
  };

  const handleConfirm = () => {
    onClose();
    onConfirm(
      {
        overrides: {
          html: (
            <ModificationAlertContent
              textItems={rowData.filter(x => selectedClassroomIds.includes(x.id)).map(x => x.name)}
              {...(confirmInstructionsI18nKey && { deleteText: t(confirmInstructionsI18nKey) })}
            />
          )
        }
      },
      selectedClassroomIds
    );
  };

  const handleCheck = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const classroomId = +event.target.value;
    if (classroomId && selectedClassroomIds.includes(classroomId)) {
      setSelectedClassroomIds(selectedClassroomIds.filter(id => id !== classroomId));
    } else {
      setSelectedClassroomIds([...selectedClassroomIds, classroomId]);
    }
  };

  return (
    <PageContainer loading={loading} error={error}>
      <div className={classes.root}>
        <div className={classes.body}>
          <StepDetails variant={'title'}>{t(titleI18nKey)}</StepDetails>
          <Container>
            <Stepper activeStep={currentStep} className={classes.stepper}>
              {STEPS.map(step => (
                <Step id={step.id}>
                  <StepLabel
                    StepIconProps={{
                      classes: { active: classes.stepIcon, completed: classes.stepIcon }
                    }}
                    classes={{ label: classes.stepLabel }}
                  >
                    {t(step.titleI18nKey)}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Container>
          <Divider />
          <StepContainer value={currentStep} index={0}>
            <Divider />
            <Container>
              <StepSubheader
                title={`1. ${t(STEPS[0].titleI18nKey)}`}
                subtitle={t(instructionsI18nKey)}
              />
              <ClassroomSelectTable
                selectedClassroomIds={selectedClassroomIds}
                classroomRowData={rowData}
                handleCheck={handleCheck}
              />
            </Container>
          </StepContainer>
          <StepContainer value={currentStep} index={1}>
            <Container>
              <StepSubheader
                title={`2. ${t(STEPS[1].titleI18nKey)}`}
                subtitle={t(reviewInstructionsI18nKey)}
              />
              <ClassroomSelectTable
                selectedClassroomIds={selectedClassroomIds}
                classroomRowData={confirmRowData}
                handleCheck={handleCheck}
              />
            </Container>
          </StepContainer>
        </div>
        <StepActionBar
          cancelText={t('Cancel', { ns: '@common' })}
          confirmText={currentStep === 0 ? t(selectButtonI18nKey) : t('Confirm', { ns: '@common' })}
          disableConfirm={selectedClassroomIds.length === 0}
          onConfirm={currentStep === 0 ? handleNext : handleConfirm}
          onCancel={onClose}
        />
      </div>
    </PageContainer>
  );
};

export default RemoveClassroomModal;
